import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Button } from '@/components/atoms'
import { PaxFullObject } from '@/models/guest-room/guest-room-assign'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import { MiwaLockStatus } from '@/constants/miwa-lock'
import { ROOM_UNSPECIFIED } from '@/constants/room-manager'
import { PaxModal } from '@/components/organisms/customer/pax-modal'
import { RoomAssignStatusSelect } from '@/components/molecules/room-manager/room-assign-status-select'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'

export type RoomPropType = EditRoomType

type NotEditFormItemProps = {
  reservation: AdminReservationResponseType['reservations']
  room: RoomPropType
  roomIndex: number
  hasSmartKeyPlugin: boolean
  canIssueMiwaCardKey: boolean
  miwaButtonType: (issueType: string) => number
  onOpenKeyCardWriteModal: (room: any) => void
  getGuestRoomKeyNumber: (room: any) => string
  onEditRoomAssignGuestName: (event: any, index: number) => void
  onEditRoomAssignPax: (event: any, index: number, type?: string) => void
  onEditPaxFull: (value: PaxFullObject, index: number) => void
  isCurrentDateSameOrBeforeCheckoutDate: boolean
  hideEdit?: boolean
  onSavedData: () => Promise<any>
}

export const NotEditFormItem: React.FC<NotEditFormItemProps> = ({
  reservation,
  room,
  roomIndex,
  miwaButtonType,
  hasSmartKeyPlugin,
  canIssueMiwaCardKey,
  onOpenKeyCardWriteModal,
  getGuestRoomKeyNumber,
  onEditRoomAssignGuestName,
  onEditRoomAssignPax,
  onEditPaxFull,
  isCurrentDateSameOrBeforeCheckoutDate,
  hideEdit,
  onSavedData,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<any>()
  const [isShowPaxModal, setIsShowPaxModal] = useState(false)

  const showRoomInRoomManager = () => {
    const search = new URLSearchParams({ reservationId: reservation.reservationId })
    // 「指定なし」は表に行が無く、渡すと予約の部屋の行へ動かなくなるため、割り当て済みの部屋だけ渡す
    if (room.guestRoomId && room.roomNumber && room.roomNumber !== ROOM_UNSPECIFIED) search.set('guestRoomId', room.guestRoomId)
    history.push({ pathname: '/room-manager/daily', search: search.toString(), state: { ...location.state } })
  }

  return (
    <tr key={roomIndex}>
      <td>{room?.guestRoomAssignShortId}</td>
      <td onClick={showRoomInRoomManager}>
        <div>
          {room.assignCheckinDate ? dayjs(room.assignCheckinDate).format(t('MM-DD')) : '-'} 〜
          {room.assignCheckoutDate ? dayjs(room.assignCheckoutDate).format(t('MM-DD')) : '-'}
        </div>
      </td>
      <td>
        {!hideEdit ? (
          <input
            css={inputGuestNameStyle}
            // TODO: Delete reservationGuestName as soon as assign_guest_name in guest_room_assign table is modified
            value={room.assignGuestName ? room.assignGuestName : room.reservationGuestName ? room.reservationGuestName : ''}
            onChange={event => onEditRoomAssignGuestName(event, roomIndex)}
          />
        ) : (
          <>{room.assignGuestName ? room.assignGuestName : room.reservationGuestName ? room.reservationGuestName : '-'}</>
        )}
      </td>
      <td>
        {!hideEdit ? (
          <>
            <div onClick={e => setIsShowPaxModal(!isShowPaxModal)}>
              <input
                css={[inputCapacityStyle, { pointerEvents: 'none' }]}
                value={room.pax ? Number(room.pax).toString() : '0'}
                onChange={event => onEditRoomAssignPax(event, roomIndex)}
                type="number"
                min={0}
              />
            </div>
            <PaxModal
              isOpen={isShowPaxModal}
              onClose={() => setIsShowPaxModal(false)}
              room={room}
              roomIndex={roomIndex}
              onEditPaxFull={onEditPaxFull}
            />
          </>
        ) : (
          <div>{room.pax || '0'}</div>
        )}
      </td>
      <td onClick={showRoomInRoomManager}>{room.typeNameJa ?? '-'}</td>
      <td onClick={showRoomInRoomManager}>{room.roomNumber ?? '-'}</td>
      <td>
        <RoomAssignStatusSelect room={room as any} onChange={onSavedData} />
      </td>
      {canIssueMiwaCardKey && room?.issueType ? (
        <td>
          <Button
            width={72}
            height={32}
            fontSize={12}
            buttonType={miwaButtonType(room?.issueType)}
            isDisabled={!isCurrentDateSameOrBeforeCheckoutDate}
            onClick={() => onOpenKeyCardWriteModal(room)}
          >
            {room?.issueType === MiwaLockStatus.ISSUE ? t('Issue') : t('Reissue')}
          </Button>
        </td>
      ) : hasSmartKeyPlugin ? (
        <td onClick={showRoomInRoomManager}>{getGuestRoomKeyNumber(room)}</td>
      ) : (
        <td>-</td>
      )}
    </tr>
  )
}

const inputGuestNameStyle = css({
  color: '#272727',
  padding: '0 10px',
  height: 32,
  borderRadius: 17,
  marginRight: 16,
  appearance: 'none',
  border: '1px solid #CCC',
  width: 'calc(100% - 16px)',
})

const inputCapacityStyle = css(inputGuestNameStyle, {
  width: 64,
})
